import { Badge, Tooltip, TooltipContent, TooltipTrigger } from "@nexus/ui";
import type { ReactNode } from "react";
import type { useTranslation } from "react-i18next";

import { DualSpendValueWithTooltip } from "@/features/opportunities/components/DualSpendValue";
import {
  formatOpportunityEngineLabel,
  formatPct,
} from "@/features/opportunities/lib/format-opportunity";
import { OpportunityActionBadge } from "@/features/opportunities/components/table/OpportunityActionBadge/OpportunityActionBadge";
import { OpportunityStatusBadge } from "@/features/opportunities/components/table/OpportunityStatusBadge/OpportunityStatusBadge";
import type { OpportunityAction } from "@/features/opportunities/types/opportunities.types";

import {
  formatPostFlightMoney,
  formatPostFlightPctBetter,
} from "../../../lib/post-flight-opportunity-metrics.js";
import {
  formatCompetitorsOfTotal,
  formatInvestmentMultiple,
  formatPercent,
} from "../../../lib/persona-opportunity-row-model.js";
import { personaOpportunityStatusLabel } from "../../../lib/persona-opportunity-tab-status.js";
import { ExpandableCellText } from "../../table/PersonaOpportunityTable/persona-opportunity-cells.js";
import { PersonaOpportunitySupportingMedia } from "../../shared/PersonaOpportunitySupportingMedia/PersonaOpportunitySupportingMedia.js";

import type { HubChromeOpportunityRow } from "./hub-chrome.types.js";
import type { OpportunityHubSelectionMix } from "./hub-chrome.types.js";
import { hubSetTypeLabel } from "./hub-set-type-label.js";
import { OpportunityHubAngleCell } from "./OpportunityHubAngleCell.js";
import { OpportunityHubStatusBadge } from "./OpportunityHubStatusBadge.js";

type Translate = ReturnType<typeof useTranslation>["t"];

/** Text columns cannot be rolled up, so a collapsed group shows this instead. */
export const NOT_AGGREGATABLE = "—";

export interface HubOpportunityGroupRollupNumbers {
  spend: number;
  creativesCount: number;
  competitorsRunningCount: number;
  competitorsTotal: number;
  stickRatePct: number | null;
  duplicatedPct: number | null;
  investmentIndex: number | null;
}

/** Collapsed header cell when the children are not all loaded. Null means the column stays a dash. */
export function formatGroupRollup(
  columnId: string,
  rollup: HubOpportunityGroupRollupNumbers,
): string | null {
  if (columnId === "spend") return formatSpend(rollup.spend);
  if (columnId === "creatives") return String(rollup.creativesCount);
  if (columnId === "competitors") {
    return formatCompetitorsOfTotal(rollup.competitorsRunningCount, rollup.competitorsTotal);
  }
  if (columnId === "stickRate") {
    return rollup.stickRatePct == null ? null : formatPercent(rollup.stickRatePct);
  }
  if (columnId === "duplicated") {
    return rollup.duplicatedPct == null ? null : formatPercent(rollup.duplicatedPct);
  }
  if (columnId === "investmentIndex") {
    return formatInvestmentMultiple(rollup.investmentIndex);
  }
  return null;
}

/** Resizable text columns stop here. The old Opportunities table has no max. */
export const HUB_TEXT_COLUMN_MAX_PX = 800;

function textColumn(size: number, minSize: number) {
  return {
    size,
    minSize,
    maxSize: HUB_TEXT_COLUMN_MAX_PX,
    enableResizing: true,
  };
}

function fixedColumn(size: number) {
  return {
    size,
    minSize: size,
    maxSize: size,
    enableResizing: false,
  };
}

export interface HubOpportunityColumn {
  id: string;
  header: string;
  /** Which set types the column is meaningful for — drives the dynamic column set. */
  scope: "all" | "persona" | "competitor";
  isNumeric?: boolean;
  size: number;
  minSize: number;
  maxSize: number;
  enableResizing: boolean;
  /** Per-cell classes the workspace table applies, including the drivenBy tint. */
  cellClassName?: string | ((row: HubChromeOpportunityRow) => string);
  cell: (row: HubChromeOpportunityRow) => ReactNode;
  /** Omitted for text columns — the group row falls back to `NOT_AGGREGATABLE`. */
  aggregate?: (rows: HubChromeOpportunityRow[]) => string;
  /** Omitted when the column carries no orderable value, which also hides the sort affordance. */
  sortValue?: (row: HubChromeOpportunityRow) => string | number | null | undefined;
  /** Plain export text. Independent of sortValue so an unsortable column still leaves the cell. */
  csvValue?: (row: HubChromeOpportunityRow) => string | number | null | undefined;
}

function sum(rows: HubChromeOpportunityRow[], pick: (row: HubChromeOpportunityRow) => number) {
  return rows.reduce((total, row) => total + pick(row), 0);
}

function mean(
  rows: HubChromeOpportunityRow[],
  pick: (row: HubChromeOpportunityRow) => number | null | undefined,
): number | null {
  const values = rows.map(pick).filter((value): value is number => value != null);
  if (values.length === 0) return null;
  return values.reduce((total, value) => total + value, 0) / values.length;
}

/** Whole dollars, matching legacy money. Sub-dollar floats such as 0.344 round to $0. */
export function formatSpend(usd: number): string {
  if (Math.abs(usd) >= 1000) return `$${Math.round(usd / 100) / 10}k`;
  return `$${Math.round(usd)}`;
}

function drivingText(row: HubChromeOpportunityRow): string {
  if (row.setType === "COMPETITOR" || row.setType === "POST_FLIGHT") return row.topicTitle ?? row.angle;
  if (row.drivenBy === "motivation") return row.motivation ?? row.angle;
  return row.painPoint ?? row.angle;
}

/** Sticky identifier column: the driving pain/motivation for personas, the topic for competitors. */
export function drivingIdentifier(row: HubChromeOpportunityRow): string {
  return drivingText(row);
}

export function drivingHeader(t: Translate, mix: OpportunityHubSelectionMix): string {
  if (mix.hasPersona && mix.hasCompetitor) {
    return t("personaOpportunities.hub.opportunities.columns.opportunity", "Opportunity");
  }
  if (mix.hasCompetitor) {
    return t("personaOpportunities.hub.opportunities.columns.topic", "Topic");
  }
  return t("personaOpportunities.hub.opportunities.columns.driving", "Driving");
}

export function buildOpportunityColumns(
  t: Translate,
  mix: OpportunityHubSelectionMix,
): HubOpportunityColumn[] {
  const valueUnavailable = NOT_AGGREGATABLE;

  const columns: HubOpportunityColumn[] = [
    {
      id: "type",
      header: t("personaOpportunities.hub.opportunities.columns.type", "Type"),
      scope: "all",
      ...fixedColumn(150),
      cell: (row) => (
        <Badge variant="outline">{hubSetTypeLabel(t, row.setType)}</Badge>
      ),
      sortValue: (row) => row.setType,
    },
    {
      id: "status",
      header: t("personaOpportunities.active.columns.status", "Status"),
      scope: "all",
      ...fixedColumn(150),
      cell: (row) => (
        <span className="inline-flex rounded-sm border border-input bg-background px-1.5 py-0.5 text-[11px] font-medium">
          {personaOpportunityStatusLabel(t, row.status)}
        </span>
      ),
      sortValue: (row) => personaOpportunityStatusLabel(t, row.status),
    },
    {
      id: "video",
      header: t("personaOpportunities.active.columns.video", "Video"),
      scope: "persona",
      ...fixedColumn(90),
      cell: (row) => <PersonaOpportunitySupportingMedia row={row} variant="table" />,
    },
    {
      id: "painPoint",
      header: t("personaOpportunities.active.columns.painPoint", "Pain point"),
      scope: "persona",
      ...textColumn(220, 140),
      cell: (row) => row.painPoint ?? valueUnavailable,
      sortValue: (row) => row.painPoint,
    },
    {
      id: "motivation",
      header: t("personaOpportunities.active.columns.motivation", "Motivation"),
      scope: "persona",
      ...textColumn(220, 140),
      cell: (row) => row.motivation ?? valueUnavailable,
      sortValue: (row) => row.motivation,
    },
    {
      id: "angle",
      header: t("personaOpportunities.active.columns.angle", "Angle"),
      scope: "persona",
      ...textColumn(220, 140),
      cell: (row) => row.angle,
      sortValue: (row) => row.angle,
    },
    {
      id: "action",
      header: t("personaOpportunities.hub.opportunities.columns.action", "Action"),
      scope: "competitor",
      ...fixedColumn(120),
      cell: (row) => (row.action ? <Badge variant="outline">{row.action}</Badge> : valueUnavailable),
      sortValue: (row) => row.action,
    },
    {
      id: "flow",
      header: t("personaOpportunities.hub.opportunities.columns.flow", "Flow"),
      scope: "competitor",
      ...textColumn(120, 100),
      cell: (row) => row.dataset ?? valueUnavailable,
      sortValue: (row) => row.dataset,
    },
    {
      id: "feature",
      header: t("personaOpportunities.hub.opportunities.columns.feature", "Feature"),
      scope: "competitor",
      ...textColumn(130, 80),
      cell: (row) => row.feature ?? valueUnavailable,
      sortValue: (row) => row.feature,
    },
    {
      id: "subFeature",
      header: t("personaOpportunities.hub.opportunities.columns.subFeature", "Sub-feature"),
      scope: "competitor",
      ...textColumn(140, 100),
      cell: (row) => row.subFeature ?? valueUnavailable,
      sortValue: (row) => row.subFeature,
    },
    {
      id: "engine",
      header: t("personaOpportunities.hub.opportunities.columns.engine", "Engine"),
      scope: "competitor",
      ...textColumn(120, 100),
      cell: (row) => row.engine ?? valueUnavailable,
      sortValue: (row) => row.engine,
    },
    {
      id: "spend",
      header: t("personaOpportunities.hub.opportunities.columns.spend", "Spend"),
      scope: "competitor",
      isNumeric: true,
      ...fixedColumn(110),
      cell: (row) => (row.spendUsd == null ? valueUnavailable : formatSpend(row.spendUsd)),
      aggregate: (rows) => formatSpend(sum(rows, (row) => row.spendUsd ?? 0)),
      sortValue: (row) => row.spendUsd,
    },
    {
      id: "why",
      header: t("personaOpportunities.active.columns.why", "Why"),
      scope: "all",
      ...textColumn(320, 160),
      cell: (row) => row.why,
    },
    {
      id: "creatives",
      header: t("personaOpportunities.active.columns.creatives", "Creatives"),
      scope: "all",
      isNumeric: true,
      ...fixedColumn(110),
      cell: (row) => row.creativesCount,
      aggregate: (rows) => String(sum(rows, (row) => row.creativesCount)),
      sortValue: (row) => row.creativesCount,
    },
    {
      id: "competitors",
      header: t("personaOpportunities.active.columns.competitors", "Competitors"),
      scope: "all",
      isNumeric: true,
      ...fixedColumn(130),
      cell: (row) => formatCompetitorsOfTotal(row.competitorsRunningCount, row.competitorsTotal),
      aggregate: (rows) =>
        formatCompetitorsOfTotal(
          sum(rows, (row) => row.competitorsRunningCount),
          sum(rows, (row) => row.competitorsTotal),
        ),
      sortValue: (row) => row.competitorsRunningCount,
    },
    {
      id: "stickRate",
      header: t("personaOpportunities.active.columns.stickRate", "Stick rate"),
      scope: "all",
      isNumeric: true,
      ...fixedColumn(110),
      cell: (row) => formatPercent(row.stickRatePct),
      aggregate: (rows) => {
        const average = mean(rows, (row) => row.stickRatePct);
        return average == null ? NOT_AGGREGATABLE : formatPercent(average);
      },
      sortValue: (row) => row.stickRatePct,
    },
    {
      id: "duplicated",
      header: t("personaOpportunities.active.columns.duplicated", "Duplicated"),
      scope: "all",
      isNumeric: true,
      ...fixedColumn(110),
      cell: (row) => formatPercent(row.duplicatedPct),
      aggregate: (rows) => {
        const average = mean(rows, (row) => row.duplicatedPct);
        return average == null ? NOT_AGGREGATABLE : formatPercent(average);
      },
      sortValue: (row) => row.duplicatedPct,
    },
    {
      id: "investmentIndex",
      header: t("personaOpportunities.active.columns.investmentIndex", "Investment index"),
      scope: "all",
      isNumeric: true,
      ...fixedColumn(150),
      cell: (row) => formatInvestmentMultiple(row.investmentIndex) ?? valueUnavailable,
      aggregate: (rows) => {
        const average = mean(rows, (row) => row.investmentIndex);
        return average == null
          ? NOT_AGGREGATABLE
          : (formatInvestmentMultiple(average) ?? NOT_AGGREGATABLE);
      },
      sortValue: (row) => row.investmentIndex,
    },
  ];

  const isMixed = mix.hasPersona && mix.hasCompetitor;
  return columns.filter((column) => {
    if (column.id === "type") return isMixed;
    if (column.scope === "persona") return mix.hasPersona;
    if (column.scope === "competitor") return mix.hasCompetitor;
    return true;
  });
}

/** Persona fold table: the angle cell carries its pain point and motivation, so the sheet fits without sideways scroll. */
export function buildPersonaTableColumns(t: Translate): HubOpportunityColumn[] {
  const valueUnavailable = t("personaOpportunities.active.valueUnavailable", "—");

  return [
    {
      id: "video",
      header: t("personaOpportunities.active.columns.video", "Video"),
      scope: "persona",
      ...fixedColumn(72),
      cell: (row) => <PersonaOpportunitySupportingMedia row={row} variant="table" />,
    },
    {
      id: "angle",
      header: t("personaOpportunities.active.columns.angle", "Angle"),
      scope: "persona",
      ...textColumn(280, 160),
      cell: (row) => <OpportunityHubAngleCell row={row} />,
      sortValue: (row) => row.angle,
    },
    {
      id: "why",
      header: t("personaOpportunities.active.columns.why", "Why"),
      scope: "persona",
      ...textColumn(240, 160),
      cellClassName: "text-xs leading-relaxed text-muted-foreground",
      cell: (row) => (
        <ExpandableCellText text={row.why} className="line-clamp-4 whitespace-normal break-words" />
      ),
    },
    {
      id: "creatives",
      header: t("personaOpportunities.active.columns.creatives", "Creatives"),
      scope: "persona",
      isNumeric: true,
      ...fixedColumn(110),
      cellClassName: "text-right tabular-nums",
      cell: (row) => row.creativesCount,
      aggregate: (rows) => String(sum(rows, (row) => row.creativesCount)),
      sortValue: (row) => row.creativesCount,
    },
    {
      id: "competitors",
      header: t("personaOpportunities.active.columns.competitors", "Competitors"),
      scope: "persona",
      isNumeric: true,
      ...fixedColumn(130),
      cellClassName: "text-right tabular-nums",
      cell: (row) => formatCompetitorsOfTotal(row.competitorsRunningCount, row.competitorsTotal),
      sortValue: (row) => row.competitorsRunningCount,
    },
    {
      id: "stickRate",
      header: t("personaOpportunities.active.columns.stickRate", "Stick rate"),
      scope: "persona",
      isNumeric: true,
      ...fixedColumn(110),
      cellClassName: "text-right tabular-nums",
      cell: (row) => formatPercent(row.stickRatePct),
      sortValue: (row) => row.stickRatePct,
    },
    {
      id: "duplicated",
      header: t("personaOpportunities.active.columns.duplicated", "Duplicated"),
      scope: "persona",
      isNumeric: true,
      ...fixedColumn(110),
      cellClassName: "text-right tabular-nums",
      cell: (row) => formatPercent(row.duplicatedPct),
      sortValue: (row) => row.duplicatedPct,
    },
    {
      id: "investmentIndex",
      header: t("personaOpportunities.active.columns.investmentIndex", "Investment index"),
      scope: "persona",
      isNumeric: true,
      ...fixedColumn(150),
      cellClassName: "text-right",
      cell: (row) => (
        <div className="space-y-0.5 whitespace-nowrap">
          <div className="text-sm font-semibold text-primary">
            {formatInvestmentMultiple(row.investmentIndex) ?? valueUnavailable}
          </div>
          <div className="text-[11px] text-muted-foreground">
            {t("personaOpportunities.active.youShare", {
              pct: formatPercent(row.myInvestmentSharePct),
              defaultValue: `You ${formatPercent(row.myInvestmentSharePct)}`,
            })}
            {" · "}
            {t("personaOpportunities.active.marketShare", {
              pct: formatPercent(row.marketInvestmentSharePct),
              defaultValue: `Market ${formatPercent(row.marketInvestmentSharePct)}`,
            })}
          </div>
        </div>
      ),
      sortValue: (row) => row.investmentIndex,
    },
    {
      id: "status",
      header: t("personaOpportunities.active.columns.status", "Status"),
      scope: "persona",
      ...fixedColumn(120),
      cell: (row) => <OpportunityHubStatusBadge status={row.status} />,
      sortValue: (row) => personaOpportunityStatusLabel(t, row.status),
    },
  ];
}

function metricText(value: string | null): string {
  return value ?? NOT_AGGREGATABLE;
}

/** Persona table shell with winning-ad columns. Used only for Post-Flight rows. */
export function buildPostFlightTableColumns(t: Translate): HubOpportunityColumn[] {
  const trendLabel = (row: HubChromeOpportunityRow) => {
    if (row.postFlight?.trend === "improving") {
      return t("personaOpportunities.postFlightOpportunity.metrics.trendImproving", "Improving");
    }
    if (row.postFlight?.trend === "worsening") {
      return t("personaOpportunities.postFlightOpportunity.metrics.trendWorsening", "Worsening");
    }
    if (row.postFlight?.trend === "flat") {
      return t("personaOpportunities.postFlightOpportunity.metrics.trendFlat", "Flat");
    }
    return null;
  };

  return [
    {
      id: "video",
      header: t("personaOpportunities.active.columns.video", "Video"),
      scope: "persona",
      ...fixedColumn(72),
      cell: (row) => <PersonaOpportunitySupportingMedia row={row} variant="table" />,
      csvValue: (row) => row.mediaUrl ?? NOT_AGGREGATABLE,
    },
    {
      id: "angle",
      header: t("personaOpportunities.postFlightOpportunity.columns.title", "Title"),
      scope: "persona",
      ...textColumn(220, 220),
      cell: (row) => (
        <ExpandableCellText text={row.angle} className="line-clamp-3 whitespace-normal break-words font-medium" />
      ),
      sortValue: (row) => row.angle,
      csvValue: (row) => row.angle,
    },
    {
      id: "instruction",
      header: t("personaOpportunities.postFlightOpportunity.columns.direction", "Direction"),
      scope: "persona",
      ...textColumn(220, 220),
      cell: (row) => (
        <ExpandableCellText
          text={row.postFlight?.instruction ?? NOT_AGGREGATABLE}
          className="line-clamp-3 whitespace-normal break-words"
        />
      ),
      csvValue: (row) => row.postFlight?.instruction ?? NOT_AGGREGATABLE,
    },
    {
      id: "parent",
      header: t("personaOpportunities.postFlightOpportunity.columns.parent", "Concept"),
      scope: "persona",
      ...textColumn(160, 160),
      cell: (row) => row.fromLabel ?? NOT_AGGREGATABLE,
      csvValue: (row) => row.fromLabel ?? NOT_AGGREGATABLE,
    },
    {
      id: "cpa",
      header: t("personaOpportunities.postFlightOpportunity.metrics.cpa", "CPA"),
      scope: "persona",
      isNumeric: true,
      ...fixedColumn(90),
      cellClassName: "text-right tabular-nums",
      cell: (row) => metricText(row.postFlight?.cpa == null ? null : formatPostFlightMoney(row.postFlight.cpa)),
      csvValue: (row) =>
        row.postFlight?.cpa == null ? NOT_AGGREGATABLE : formatPostFlightMoney(row.postFlight.cpa),
    },
    {
      id: "accountCpa",
      header: t("personaOpportunities.postFlightOpportunity.metrics.accountCpa", "Account CPA"),
      scope: "persona",
      isNumeric: true,
      ...fixedColumn(110),
      cellClassName: "text-right tabular-nums",
      cell: (row) =>
        metricText(row.postFlight?.accountCpa == null ? null : formatPostFlightMoney(row.postFlight.accountCpa)),
      csvValue: (row) =>
        row.postFlight?.accountCpa == null
          ? NOT_AGGREGATABLE
          : formatPostFlightMoney(row.postFlight.accountCpa),
    },
    {
      id: "pctBetter",
      header: t("personaOpportunities.postFlightOpportunity.metrics.pctBetter", "% better"),
      scope: "persona",
      isNumeric: true,
      ...fixedColumn(90),
      cellClassName: "text-right tabular-nums",
      cell: (row) =>
        metricText(row.postFlight?.pctBetter == null ? null : formatPostFlightPctBetter(row.postFlight.pctBetter)),
      csvValue: (row) =>
        row.postFlight?.pctBetter == null
          ? NOT_AGGREGATABLE
          : formatPostFlightPctBetter(row.postFlight.pctBetter),
    },
    {
      id: "purchases",
      header: t("personaOpportunities.postFlightOpportunity.metrics.purchases", "Purchases"),
      scope: "persona",
      isNumeric: true,
      ...fixedColumn(100),
      cellClassName: "text-right tabular-nums",
      cell: (row) =>
        metricText(row.postFlight?.purchases == null ? null : Math.round(row.postFlight.purchases).toLocaleString("en-US")),
      csvValue: (row) =>
        row.postFlight?.purchases == null
          ? NOT_AGGREGATABLE
          : Math.round(row.postFlight.purchases).toLocaleString("en-US"),
    },
    {
      id: "spend",
      header: t("personaOpportunities.postFlightOpportunity.metrics.spend", "Spend"),
      scope: "persona",
      isNumeric: true,
      ...fixedColumn(90),
      cellClassName: "text-right tabular-nums",
      cell: (row) => metricText(row.postFlight?.spend == null ? null : formatPostFlightMoney(row.postFlight.spend)),
      csvValue: (row) =>
        row.postFlight?.spend == null ? NOT_AGGREGATABLE : formatPostFlightMoney(row.postFlight.spend),
    },
    {
      id: "adRank",
      header: t("personaOpportunities.postFlightOpportunity.metrics.rank", "Rank"),
      scope: "persona",
      isNumeric: true,
      ...fixedColumn(70),
      cellClassName: "text-right tabular-nums",
      cell: (row) => metricText(row.postFlight?.adRank == null ? null : `#${Math.round(row.postFlight.adRank)}`),
      csvValue: (row) =>
        row.postFlight?.adRank == null ? NOT_AGGREGATABLE : `#${Math.round(row.postFlight.adRank)}`,
    },
    {
      id: "trend",
      header: t("personaOpportunities.postFlightOpportunity.metrics.trend", "Trend"),
      scope: "persona",
      ...fixedColumn(110),
      cell: (row) => trendLabel(row) ?? NOT_AGGREGATABLE,
      csvValue: (row) => trendLabel(row) ?? NOT_AGGREGATABLE,
    },
    {
      id: "why",
      header: t("personaOpportunities.active.columns.why", "Why"),
      scope: "persona",
      ...textColumn(200, 200),
      cell: (row) => (
        <ExpandableCellText text={row.why} className="line-clamp-3 whitespace-normal break-words" />
      ),
      csvValue: (row) => row.why || NOT_AGGREGATABLE,
    },
    {
      id: "status",
      header: t("personaOpportunities.active.columns.status", "Status"),
      scope: "persona",
      ...fixedColumn(140),
      cell: (row) => <OpportunityHubStatusBadge status={row.status} />,
      sortValue: (row) => personaOpportunityStatusLabel(t, row.status),
      csvValue: (row) => personaOpportunityStatusLabel(t, row.status),
    },
  ];
}

function truncated(text: string | null | undefined, className: string): ReactNode {
  const value = text?.trim() ? text : NOT_AGGREGATABLE;
  return <span className={className}>{value}</span>;
}

function truncatedWithTooltip(text: string | null | undefined, className: string): ReactNode {
  if (!text?.trim()) return truncated(text, className);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={className}>{text}</span>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-sm">
        {text}
      </TooltipContent>
    </Tooltip>
  );
}

const WRAPPED_TEXT_CELL = "line-clamp-2 whitespace-normal break-words text-sm text-muted-foreground";
const MUTED_TEXT_CELL = "block truncate text-sm text-muted-foreground";

function signedPercent(value: number | null | undefined): string {
  return value == null ? NOT_AGGREGATABLE : formatPct(value);
}

const OPPORTUNITY_ACTIONS: readonly OpportunityAction[] = ["launch", "scale", "test"];

export function asOpportunityAction(action: string | null | undefined): OpportunityAction | null {
  return OPPORTUNITY_ACTIONS.find((known) => known === action) ?? null;
}

const FINDINGS_PERCENT_RE = /([0-9]+(?:\.[0-9]+)?)%/;

/** Same first-percent extract the hub list uses, so the column sorts by gap rather than text. */
function findingsGap(findings: string | null | undefined): number | null {
  if (findings == null || findings === "") return null;
  const match = FINDINGS_PERCENT_RE.exec(findings);
  if (!match?.[1]) return null;
  const gap = Number(match[1]);
  return Number.isFinite(gap) ? gap : null;
}

/** No-persona table: the legacy opportunities columns, in that order. Spend is a share, shown as a percent. */
export function buildLegacyTableColumns(
  t: Translate,
  industryLabelByIndex: ReadonlyMap<string, string> = new Map(),
): HubOpportunityColumn[] {
  return [
    {
      id: "state",
      header: t("opportunities.table.status", "Status"),
      scope: "competitor",
      ...fixedColumn(110),
      cell: (row) => <OpportunityStatusBadge status={row.legacyState === "rejected" ? "rejected" : "active"} />,
      sortValue: (row) => row.legacyState ?? "active",
    },
    {
      id: "flow",
      header: t("opportunities.table.flowName", "Flow"),
      scope: "competitor",
      ...textColumn(150, 100),
      cell: (row) => truncatedWithTooltip(row.dataset, MUTED_TEXT_CELL),
      sortValue: (row) => row.dataset,
    },
    {
      id: "topicTitle",
      header: t("opportunities.table.topicTitle", "Topic title"),
      scope: "competitor",
      ...textColumn(280, 160),
      cell: (row) =>
        truncatedWithTooltip(row.topicTitle ?? row.angle, "block truncate font-medium text-foreground"),
      sortValue: (row) => row.topicTitle ?? row.angle,
    },
    {
      id: "topicDescription",
      header: t("opportunities.table.topicDescription", "Topic description"),
      scope: "competitor",
      ...textColumn(360, 200),
      cell: (row) => truncatedWithTooltip(row.topicDescription, WRAPPED_TEXT_CELL),
      sortValue: (row) => row.topicDescription,
    },
    {
      id: "findings",
      header: t("opportunities.table.findings", "Findings"),
      scope: "competitor",
      ...textColumn(180, 120),
      cell: (row) => truncatedWithTooltip(row.findings, MUTED_TEXT_CELL),
      sortValue: (row) => findingsGap(row.findings),
    },
    {
      id: "action",
      header: t("opportunities.table.action", "Action"),
      scope: "competitor",
      ...fixedColumn(110),
      cell: (row) => <OpportunityActionBadge action={asOpportunityAction(row.action)} />,
      sortValue: (row) => row.action,
    },
    {
      id: "suggestion",
      header: t("opportunities.table.suggestion", "Suggestion"),
      scope: "competitor",
      ...textColumn(360, 200),
      cell: (row) => truncatedWithTooltip(row.suggestion, WRAPPED_TEXT_CELL),
      sortValue: (row) => row.suggestion,
    },
    {
      id: "spend",
      header: t("opportunities.table.spend", "My spend"),
      scope: "competitor",
      ...fixedColumn(140),
      cell: (row) => (
        <DualSpendValueWithTooltip
          perDataset={row.spendPerAngle ?? null}
          feature={row.feature}
          subFeature={row.subFeature}
          totalCreatives={row.spendUsd ?? null}
        />
      ),
      sortValue: (row) => row.spendUsd,
    },
    {
      id: "benchmarkSpend",
      header: t("opportunities.table.benchmarkSpend", "Benchmark spend"),
      scope: "competitor",
      ...fixedColumn(160),
      cell: (row) => (
        <DualSpendValueWithTooltip
          perDataset={row.benchmarkSpendPerAngle ?? null}
          feature={row.feature}
          subFeature={row.subFeature}
          totalCreatives={row.benchmarkSpendUsd ?? null}
        />
      ),
      sortValue: (row) => row.benchmarkSpendUsd,
    },
    {
      id: "competitorSpend",
      header: t("opportunities.table.competitorSpend", "Competitor spend"),
      scope: "competitor",
      ...fixedColumn(160),
      cell: (row) => (
        <DualSpendValueWithTooltip
          perDataset={row.competitorSpendPerAngle ?? null}
          feature={row.feature}
          subFeature={row.subFeature}
          totalCreatives={row.competitorSpendUsd ?? null}
        />
      ),
      sortValue: (row) => row.competitorSpendUsd,
    },
    {
      id: "feature",
      header: t("opportunities.table.feature", "Feature"),
      scope: "competitor",
      ...textColumn(140, 80),
      cell: (row) => truncatedWithTooltip(row.feature, MUTED_TEXT_CELL),
      sortValue: (row) => row.feature,
    },
    {
      id: "subFeature",
      header: t("opportunities.table.subFeature", "Sub-feature"),
      scope: "competitor",
      ...textColumn(150, 120),
      cell: (row) => truncatedWithTooltip(row.subFeature, MUTED_TEXT_CELL),
      sortValue: (row) => row.subFeature,
    },
    {
      id: "kpi",
      header: t("opportunities.table.kpi", "KPI"),
      scope: "competitor",
      ...fixedColumn(90),
      cellClassName: "text-right font-mono tabular-nums text-sm text-muted-foreground",
      cell: (row) => signedPercent(row.kpi),
      sortValue: (row) => row.kpi,
    },
    {
      id: "competitorKpi",
      header: t("opportunities.table.competitorKpi", "Competitor KPI"),
      scope: "competitor",
      ...fixedColumn(140),
      cellClassName: "text-right font-mono tabular-nums text-sm text-muted-foreground",
      cell: (row) => signedPercent(row.competitorKpi),
      sortValue: (row) => row.competitorKpi,
    },
    {
      id: "benchmarkKpi",
      header: t("opportunities.table.benchmarkKpi", "Benchmark KPI"),
      scope: "competitor",
      ...fixedColumn(140),
      cellClassName: "text-right font-mono tabular-nums text-sm text-muted-foreground",
      cell: (row) => signedPercent(row.benchmarkKpi),
      sortValue: (row) => row.benchmarkKpi,
    },
    {
      id: "engine",
      header: t("opportunities.table.engine", "Engine"),
      scope: "competitor",
      ...textColumn(140, 100),
      cell: (row) => (
        <span className={MUTED_TEXT_CELL}>
          {row.engine ? formatOpportunityEngineLabel(row.engine, t) : NOT_AGGREGATABLE}
        </span>
      ),
      sortValue: (row) => row.engine,
    },
    {
      id: "industry",
      header: t("opportunities.table.industry", "Industry"),
      scope: "competitor",
      ...textColumn(160, 100),
      cell: (row) => (
        <span className={MUTED_TEXT_CELL}>
          {row.industry
            ? (industryLabelByIndex.get(row.industry) ?? row.industry)
            : NOT_AGGREGATABLE}
        </span>
      ),
      sortValue: (row) =>
        row.industry ? (industryLabelByIndex.get(row.industry) ?? row.industry) : null,
    },
    {
      id: "runDate",
      header: t("opportunities.table.runDate", "Run date"),
      scope: "competitor",
      ...fixedColumn(130),
      cell: (row) => {
        if (!row.runDate) return NOT_AGGREGATABLE;
        const formatted = new Date(row.runDate).toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
        return <span className="text-sm text-muted-foreground">{formatted}</span>;
      },
      sortValue: (row) => row.runDate,
    },
  ];
}
